import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { DumbbellIcon, LeafIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export const HeroSection: React.FC = () => {
  const [mobileImageLoaded, setMobileImageLoaded] = useState(false);
  useEffect(() => {
    // Preload image for mobile devices
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      const img = new Image();
      img.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/kC63pETuZHqEvxkQyD1w2n/Ondra-22_%282%29.jpg';
      img.onload = () => {
        setMobileImageLoaded(true);
      };
    } else {
      setMobileImageLoaded(true); // Desktop doesn't need to wait
    }
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // Height of fixed navbar
      const additionalOffset = 20; // Extra spacing for better visibility
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - additionalOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-screen bg-black overflow-hidden pt-20 section section-dark" data-id="element-429">
      <div className="flex flex-col md:flex-row relative z-10" data-id="element-430">
        {/* Mobile hero - full image with overlay text */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: mobileImageLoaded ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="md:hidden w-full relative h-[100vh]" data-id="element-431">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kC63pETuZHqEvxkQyD1w2n/Ondra-22_%282%29.jpg" alt="Transformace těla" className="w-full h-full object-cover" loading="eager" onLoad={() => setMobileImageLoaded(true)} data-id="element-432" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/90" data-id="element-433"></div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: mobileImageLoaded ? 1 : 0,
          y: mobileImageLoaded ? 0 : 20
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="absolute inset-0 flex flex-col justify-center items-center p-6 text-center" data-id="element-434">
            <h1 className="text-3xl font-black mb-4" data-id="element-435">
              <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent" data-id="element-436">
                TRANSFORMUJ
              </span>{' '}
              SVÉ TĚLO
            </h1>
            <p className="text-base font-medium text-white mb-8 max-w-xs" data-id="element-437">
              Nauč se za{' '}
              <span className="text-green-500 font-semibold" data-id="element-438">3 měsíce</span>{' '}
              cvičit tak, aby ses už nikdy nemusel spoléhat na{' '}
              <span className="text-green-500 font-semibold" data-id="element-439">trenéra</span> ani{' '}
              <span className="text-green-500 font-semibold" data-id="element-440">
                subscription plány
              </span>
              .
            </p>
            <div className="flex justify-center w-full max-w-xs" data-id="element-441">
              <Button variant="outline" size="lg" onClick={() => scrollToSection('plan-cards')} icon={<DumbbellIcon className="w-5 h-5" data-id="element-443" />} className="text-sm py-4 border-white hover:bg-green-500 hover:border-green-500 transition-colors" data-id="element-442">
                MÁM ZÁJEM O PLÁN
              </Button>
            </div>
          </motion.div>
        </motion.div>
        {/* Mobile - Additional text below hero */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: mobileImageLoaded ? 1 : 0,
        y: mobileImageLoaded ? 0 : 20
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="md:hidden bg-black px-6 py-10" data-id="element-444">
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-xl md:text-2xl mb-6 font-semibold text-white tracking-wide" data-id="element-445">
            Pokud máš pocit, že:
          </motion.p>
          <motion.ul initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="space-y-5 mb-8 text-lg" data-id="element-446">
            <li className="flex items-start" data-id="element-447">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-448">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-449">
                Cvičíš roky, ale{' '}
                <span className="text-green-500 font-semibold" data-id="element-450">nerosteš</span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-451">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-452">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-453">
                Nevíš, jak{' '}
                <span className="text-green-500 font-semibold" data-id="element-454">
                  správně cvičit
                </span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-455">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-456">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-457">
                Nechceš utrácet za{' '}
                <span className="text-green-500 font-semibold" data-id="element-458">drahé</span>{' '}
                soukromé trénery, nebo subscription plány
              </span>
            </li>
          </motion.ul>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-xl md:text-2xl mb-12 font-semibold text-white tracking-wide" data-id="element-459">
            Pak jsi tady <span className="text-green-500" data-id="element-460">správně</span>.
          </motion.p>
        </motion.div>
        {/* Desktop layout - left side image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8
      }} className="hidden md:block w-full md:w-1/2 h-[50vh] md:h-screen relative" data-id="element-461">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kC63pETuZHqEvxkQyD1w2n/Ondra-22_%282%29.jpg" alt="Transformace těla" className="w-full h-full object-cover object-center" loading="eager" data-id="element-462" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black" data-id="element-463"></div>
        </motion.div>
        {/* Desktop layout - right side content */}
        <div className="hidden md:block w-full md:w-1/2 md:pl-12 container mx-auto px-4 py-8 md:py-20" data-id="element-464">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-3xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight" data-id="element-465">
            <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent" data-id="element-466">
              TRANSFORMUJ
            </span>{' '}
            SVÉ TĚLO
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl mb-6 font-medium text-gray-200" data-id="element-467">
            Nauč se za{' '}
            <span className="text-green-500 font-semibold" data-id="element-468">3 měsíce</span>{' '}
            cvičit tak, aby ses už{' '}
            <span className="text-green-500 font-semibold" data-id="element-469">nikdy</span> nemusel
            spoléhat na své{' '}
            <span className="text-green-500 font-semibold" data-id="element-470">
              dosavadní zkušenosti
            </span>
            , <span className="text-green-500 font-semibold" data-id="element-471">trenéra</span> ani{' '}
            <span className="text-green-500 font-semibold" data-id="element-472">nekonečné</span>{' '}
            <span className="text-green-500 font-semibold" data-id="element-473">
              subscription plány
            </span>
            .
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-xl md:text-2xl mb-6 font-semibold text-white tracking-wide" data-id="element-474">
            Pokud máš pocit, že:
          </motion.p>
          <motion.ul initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="space-y-5 mb-8 text-lg" data-id="element-475">
            <li className="flex items-start" data-id="element-476">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-477">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-478">
                Cvičíš roky, ale{' '}
                <span className="text-green-500 font-semibold" data-id="element-479">nerosteš</span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-480">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-481">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-482">
                Nevíš, jak{' '}
                <span className="text-green-500 font-semibold" data-id="element-483">
                  správně cvičit
                </span>
              </span>
            </li>
            <li className="flex items-start" data-id="element-484">
              <span className="text-gray-400 mr-4 text-xl mt-1" data-id="element-485">–</span>
              <span className="text-gray-200 leading-relaxed" data-id="element-486">
                Nechceš utrácet za{' '}
                <span className="text-green-500 font-semibold" data-id="element-487">drahé</span>{' '}
                soukromé trénery, nebo subscription plány
              </span>
            </li>
          </motion.ul>
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="text-xl md:text-2xl mb-12 font-semibold text-white tracking-wide" data-id="element-488">
            Pak jsi tady <span className="text-green-500" data-id="element-489">správně</span>.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="flex justify-start mt-20" data-id="element-490">
            <Button variant="primary" size="lg" onClick={() => scrollToSection('plan-cards')} icon={<DumbbellIcon className="w-6 h-6" data-id="element-492" />} className="shine px-12 py-5 text-xl" data-id="element-491">
              MÁM ZÁJEM O PLÁN
            </Button>
          </motion.div>
        </div>
      </div>
      <div className="scroll-indicator hidden md:flex" data-id="element-493">
        <span className="text-sm tracking-widest text-green-500 font-medium" data-id="element-494">
          SCROLL
        </span>
      </div>
      <div className="section-divider divider-slant-down" data-id="element-495"></div>
    </section>;
};